import { Fragment, useContext, useMemo } from 'react'
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined'
import { BaseWidget } from './BaseWidget'
import { PeriodOverrideControl } from '../controls/PeriodOverrideControl'
import { SelectControl } from '../controls/SelectControl'
import { ColorControl } from '../controls/ColorControl'
import { NumberCssControl } from '../controls/NumberCssControl'
import useSharedCollection from '../../../../firebase/useSharedCollection'
import useSharedCollectionWhere from '../../../../firebase/useSharedCollectionWhere'
import { useFirestoreQuery } from '../../../../firebase/useFirestore'
import useSettings from '../../../../hooks/Settings'
import Hostname from '../../../../hooks/Hostname'
import ReportPrintContext from '../../../../StateManagment/ReportPrintContext'
import computeSisaIndicators from '../../../../helpers/liveReportHouseVisits/computeSisaIndicators'
import { buildSisaRows, buildSisaNotes, sisaYearFor, latestRegistrationYear, sisaKeyFigure, SISA_KEY_FIGURES } from '../../../../helpers/liveReportHouseVisits/sisaIndicatorRows'
import downloadXLSX from '../../../../helpers/export/downloadXLSX'
import ModulePlaceholder from './_shared/ModulePlaceholder'
import { TemplateDataContext } from '../context/TemplateDataContext'
import useWidgetPeriod from './_shared/useWidgetPeriod'

// Een echt Excel-bestand en geen csv: in een Nederlandse Excel landt een csv
// in één kolom, en de kolom 'Getal' blijft hier een getal om mee te rekenen.
const EXPORT_HEADERS = ['Nr', 'Indicator', 'Waarde', 'Getal']

/**
 * De acht verantwoordingsindicatoren van de SiSa/SPUK-regeling energiearmoede
 * voor het jaar van de gekozen periode. Vier weergaven van dezelfde berekening:
 * de tabel, één kerncijfer, de kanttekeningen en de downloadknop — zo kan de
 * template 'SiSa/SPUK-verantwoording' (vergrendeld, T25) het formulier vervangen
 * met een opgemaakte rapportage, en blijft het Excel-bestand één klik weg.
 * AI-CONTEXT: See docs/features/huishouden-vaste-gegevens.md (T24)
 * @extends {BaseWidget}
 */
export class HouseVisitsSisaWidget extends BaseWidget {
    getName() { return 'SiSa/SPUK-verantwoording' }
    getCategory() { return 'huishoudens' }
    getRequiredModule() { return 'housevisits' }
    getIcon() { return <FactCheckOutlinedIcon /> }

    getControls() {
        return {
            dateRange: new PeriodOverrideControl({
                label: 'Periode',
                description: 'Het jaar van het einde van de periode is het verantwoordingsjaar.',
                defaultValue: { mode: 'inherit', start: null, end: null },
            }),
            display: new SelectControl({
                label: 'Weergave',
                description: 'De hele tabel, één kerncijfer, de kanttekeningen of alleen de downloadknop.',
                defaultValue: 'table',
                options: {
                    choices: [
                        { value: 'table', label: 'Tabel' },
                        { value: 'figure', label: 'Kerncijfer' },
                        { value: 'notes', label: 'Kanttekeningen' },
                        { value: 'download', label: 'Download' },
                    ],
                },
            }),
            figure: new SelectControl({
                label: 'Kerncijfer',
                description: 'Welk getal de weergave Kerncijfer toont.',
                defaultValue: 'huishoudens',
                options: { choices: SISA_KEY_FIGURES.map((f) => ({ value: f.key, label: f.label })) },
            }),
            color: new ColorControl({
                label: 'Kleur getal',
                description: 'Kleur van het kerncijfer.',
                defaultValue: '#04342c',
            }),
            labelColor: new ColorControl({
                label: 'Kleur tekst',
                description: 'Kleur van het label en de kanttekeningen.',
                defaultValue: '#5b6770',
            }),
            size: new NumberCssControl({
                label: 'Grootte getal',
                description: '',
                defaultValue: { value: 44, unit: 'px' },
                options: { min: 12, max: 160 },
            }),
        }
    }

    render(props) {
        return <Renderer {...props} />
    }
}

/**
 * @param {object} props
 * @param {Array<import('../../../../helpers/liveReportHouseVisits/sisaIndicatorRows').SisaRow>} props.rows
 * @param {number} props.year
 * @returns {JSX.Element}
 */
const SisaTable = ({ rows, year }) => (
    <>
        <h3>SiSa/SPUK-verantwoording {year}</h3>
        <div className="housevisits-report-table-wrapper">
            <table className="housevisits-report-table">
                <thead>
                    <tr>
                        <th>Nr</th>
                        <th>Indicator</th>
                        <th>Waarde</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, i) => (
                        <Fragment key={row.id}>
                            {(i === 0 || rows[i - 1].group !== row.group) && (
                                <tr className="housevisits-sisa-group">
                                    <th colSpan={3} scope="colgroup">{row.group}</th>
                                </tr>
                            )}
                            <tr>
                                <td>{row.nr}</td>
                                <td>{row.label}</td>
                                <td>{row.value}</td>
                            </tr>
                        </Fragment>
                    ))}
                </tbody>
            </table>
        </div>
    </>
)

/**
 * @param {object} props
 * @param {{label: string, value: string}} props.figure - Uit sisaKeyFigure
 * @param {string} [props.color] - Kleur van het getal
 * @param {string} [props.labelColor] - Kleur van het label
 * @param {{value: number, unit: string}} [props.size] - Grootte van het getal
 * @returns {JSX.Element}
 */
const SisaFigure = ({ figure, color, labelColor, size }) => (
    <div className="housevisits-sisa-figure">
        {/* Kleur en grootte zijn instellingen van de widget, zoals bij 'Anekdote'. */}
        <span className="housevisits-sisa-figure__value" style={{ color, fontSize: size && `${size.value}${size.unit}` }}>{figure.value}</span>
        <span className="housevisits-sisa-figure__label" style={{ color: labelColor }}>{figure.label}</span>
    </div>
)

/**
 * @param {object} props
 * @param {Array<string>} props.notes - Uit buildSisaNotes
 * @param {string} [props.labelColor]
 * @returns {JSX.Element}
 */
const SisaNotes = ({ notes, labelColor }) => (notes.length > 0 ? (
    <ul className="housevisits-sisa-notes" style={{ color: labelColor }}>
        {notes.map((note) => <li key={note}>{note}</li>)}
    </ul>
) : (
    <p className="housevisits-sisa-notes" style={{ color: labelColor }}>Alle huishoudens tellen mee en alle bedragen zijn bekend.</p>
))

/**
 * @param {object} props
 * @param {{mode?: string, start: string|null, end: string|null}} [props.dateRange] - Eigen periode of die van het rapport
 * @param {'table'|'figure'|'notes'|'download'} [props.display] - Welke weergave
 * @param {string} [props.figure] - Sleutel uit SISA_KEY_FIGURES, voor de weergave Kerncijfer
 * @param {string} [props.color] - Kleur van het kerncijfer
 * @param {string} [props.labelColor] - Kleur van label en kanttekeningen
 * @param {{value: number, unit: string}} [props.size] - Grootte van het kerncijfer
 * @returns {JSX.Element|null}
 */
const Renderer = ({ dateRange, display = 'table', figure = 'huishoudens', color, labelColor, size }) => {
    const { housevisits } = useSettings()
    // In een template: de gegevens van Stichting Template uit het templatepakket
    // (exportTemplateData → households), zoals 'Effect' en 'Output'. De kijker is
    // bij een ándere organisatie ingelogd, dus zelf lezen gaf diens gegevens.
    const { templateData, isTemplate, error: templateError } = useContext(TemplateDataContext)
    const printing = useContext(ReportPrintContext)
    const host = Hostname()
    const brand = (host && host.Name) || ''

    const adresses = useSharedCollection(isTemplate ? '' : 'Adresses')
    const adressMaterials = useSharedCollection(isTemplate ? '' : 'AdressMaterials')
    // Ook verwijderde materialen: een regel bij een huishouden kan ernaar wijzen.
    const materials = useFirestoreQuery('Materials', { includeDeleted: true, enabled: !isTemplate })
    const tasks = useSharedCollectionWhere('Tasks', 'Type', 'addressVisit')
    const metaDocs = useFirestoreQuery('CompagnyMeta', { enabled: !isTemplate })
    const { range } = useWidgetPeriod(dateRange)

    // Elke bron levert eerst iets dat geen lijst is; pas als ze er allemaal zijn
    // staat er een getal. Een 0 tijdens het laden belandt anders in een verantwoording.
    // AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
    const loaded = [adresses, adressMaterials, materials, tasks, metaDocs].every(Array.isArray)
    const templateHouseholds = isTemplate ? (templateData?.data?.households || null) : null
    // Een template zonder periode toont het laatste jaar van zijn voorbeeld-
    // gegevens; anders stonden er na de jaarwisseling alleen nullen.
    const year = sisaYearFor(range, new Date(), templateHouseholds ? latestRegistrationYear(templateHouseholds.adressMaterials) : null)
    // Een mislukt pakket, of een pakket zonder huishoudgegevens, is een fout en
    // geen eeuwige laadtekst.
    const templateFailed = isTemplate && Boolean(templateError || (templateData && !templateHouseholds))

    const indicators = useMemo(() => {
        if (isTemplate) {
            if (!templateHouseholds) return null
            // Merk leeg: de soorten van de template-organisatie zijn eigen soorten.
            return computeSisaIndicators({ ...templateHouseholds, brand: '', year })
        }
        if (!loaded) return null
        return computeSisaIndicators({ adresses, adressMaterials, materials, tasks, meta: metaDocs[0], brand, year })
    }, [isTemplate, templateHouseholds, loaded, adresses, adressMaterials, materials, tasks, metaDocs, brand, year])
    const rows = useMemo(() => (indicators ? buildSisaRows(indicators) : []), [indicators])
    const notes = useMemo(() => (indicators ? buildSisaNotes(indicators) : []), [indicators])

    if (templateFailed) return <p className="output-widget-error">Fout bij laden template data.</p>
    if (!isTemplate && housevisits !== true) return <ModulePlaceholder mode="disabled" moduleLabel="Huishoudens" />

    const downloadHandler = () => downloadXLSX(
        rows.map((r) => ({ Nr: r.nr, Indicator: r.label, Waarde: r.value, Getal: r.raw ?? '' })),
        `sisa-spuk-verantwoording-${year}.xlsx`,
        EXPORT_HEADERS,
    )

    if (!indicators) return <div className="housevisits-report-table-empty">Gegevens laden…</div>

    const downloadButton = !printing && (
        <button type="button" className="housevisits-sisa-download" onClick={downloadHandler}>Download als Excel</button>
    )

    if (display === 'figure') {
        return <SisaFigure figure={sisaKeyFigure(indicators, figure)} color={color} labelColor={labelColor} size={size} />
    }
    if (display === 'notes') return <SisaNotes notes={notes} labelColor={labelColor} />
    // In een PDF is er niets te klikken.
    if (display === 'download') return downloadButton || null

    return (
        <div className="housevisits-sisa">
            <SisaTable rows={rows} year={year} />
            {notes.length > 0 && <SisaNotes notes={notes} />}
            {downloadButton}
        </div>
    )
}
